/** The stored preference. "system" is resolved to a concrete theme before it is applied,
 *  so no stylesheet in this repo ever needs prefers-color-scheme. */
export type ThemePref = 'dark' | 'light' | 'system';
export type Theme = 'dark' | 'light';

export const THEME_KEY = 'rypt.theme';
export const DEFAULT_PREF: ThemePref = 'dark';

const isPref = (v: unknown): v is ThemePref => v === 'dark' || v === 'light' || v === 'system';

/** Never throws. localStorage access throws outright in some privacy modes. */
export function readPref(): ThemePref {
  try {
    const raw = localStorage.getItem(THEME_KEY);
    return isPref(raw) ? raw : DEFAULT_PREF;
  } catch {
    return DEFAULT_PREF;
  }
}

/** Best effort. A theme that fails to persist is a smaller problem than a crash. */
export function writePref(pref: ThemePref): void {
  try {
    localStorage.setItem(THEME_KEY, pref);
  } catch {
    /* private mode, or storage disabled */
  }
}

export const SYSTEM_LIGHT_QUERY = '(prefers-color-scheme: light)';

export function resolve(pref: ThemePref): Theme {
  if (pref !== 'system') return pref;
  try {
    return window.matchMedia(SYSTEM_LIGHT_QUERY).matches ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
}

export function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme);
}
